import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { PlusIcon } from '@heroicons/react/24/outline'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/newRedesign/primitives/Dialog'
import { Button } from '@/components/newRedesign/primitives/Button'
import {
  FormSearchableSelect,
  FormSwitch,
  FormTextarea,
  FormInput,
} from '@/components/newRedesign/form'
import { SearchableSelectOption } from '@/components/newRedesign/components/SearchableSelect'
import { containerStatusOptions } from '../../filters/constants/statusOptions'

interface DefaultItem {
  id: string | number
  name: string
  description?: string
  observations?: string
  code?: string
}

interface EditDetailsFormData {
  status?: string
  name: string
  operationNumber: string
  countryFromId?: number
  categories?: number[]
  isReturned: boolean
  ownerCompanyId?: number
  managerCompanyId?: number
  observations?: string
}

interface EditDetailsDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  onSave: (_data: EditDetailsFormData) => void
  onCancel: () => void
  isPending?: boolean
  initialData?: Partial<EditDetailsFormData>
  // Fetchers para los selects
  fetchCountries: (_search: string) => Promise<SearchableSelectOption[]>
  fetchCategories: (_search: string) => Promise<SearchableSelectOption[]>
  fetchCompanies: (_search: string) => Promise<SearchableSelectOption[]>
  fetchManagerCompanies: (_search: string) => Promise<SearchableSelectOption[]>
  // DefaultValues para los selects
  defaultStatus?: DefaultItem
  defaultCountry?: DefaultItem
  defaultCategories?: DefaultItem[]
  defaultOwnerCompany?: DefaultItem
  defaultManagerCompany?: DefaultItem
  // Handlers para crear nuevos items
  onAddCountry?: () => void
  onAddCategory?: () => void
  onAddCompany?: () => void
  onAddManagerCompany?: () => void
}

export function EditDetailsDialog({
  open,
  onOpenChange,
  onSave,
  onCancel,
  isPending = false,
  initialData,
  fetchCountries,
  fetchCategories,
  fetchCompanies,
  fetchManagerCompanies,
  defaultStatus,
  defaultCountry,
  defaultCategories,
  defaultOwnerCompany,
  defaultManagerCompany,
  onAddCountry,
  onAddCategory,
}: EditDetailsDialogProps) {
  const { control, handleSubmit, reset } = useForm<EditDetailsFormData>({
    defaultValues: {
      status: '',
      name: '',
      operationNumber: '',
      countryFromId: undefined,
      categories: [],
      isReturned: false,
      ownerCompanyId: undefined,
      managerCompanyId: undefined,
      observations: '',
    },
  })

  useEffect(() => {
    if (open && initialData && !isPending) {
      reset({
        status: initialData.status,
        name: initialData.name || '',
        operationNumber: initialData.operationNumber || '',
        countryFromId: initialData.countryFromId,
        categories: initialData.categories || [],
        isReturned: initialData.isReturned || false,
        ownerCompanyId: initialData.ownerCompanyId,
        managerCompanyId: initialData.managerCompanyId,
        observations: initialData.observations || '',
      })
    }
  }, [open, initialData, isPending, reset])

  const handleFormSubmit = (data: EditDetailsFormData) => {
    onSave(data)
  }

  const handleClose = () => {
    if (!isPending) {
      onCancel()
      onOpenChange(false)
    }
  }

  return (
    <Dialog onOpenChange={handleClose} open={open}>
      <DialogContent
        className={
          'flex max-h-[90vh] w-full max-w-[90vw] flex-col overflow-hidden ' +
          'rounded-lg bg-neutral-50 p-0 shadow-lg md:max-h-[800px] md:w-[800px]'
        }
      >
        {/* Header */}
        <DialogHeader className="flex-row items-center justify-between border-b border-neutral-300 p-3 md:p-4">
          <DialogTitle className="text-xl font-semibold tracking-tight text-neutral-800 md:text-xl">
            Detalles
          </DialogTitle>
          <DialogDescription className="sr-only">
            Editar los detalles del contenedor incluyendo estado, país de
            origen, categorías y más
          </DialogDescription>
        </DialogHeader>

        {/* Content */}
        <form
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          id="edit-details-form"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <div className="flex-1 space-y-3 overflow-y-auto bg-neutral-50 p-3 md:space-y-4 md:p-4">
            {/* Estado */}
            <div className="flex flex-col gap-2">
              <FormSearchableSelect<EditDetailsFormData>
                control={control}
                defaultValue={defaultStatus}
                fetcher={async (search) => {
                  const options = containerStatusOptions
                    .filter((item) => item.label !== 'Todos')
                    .map((item) => ({ id: item.value, label: item.label }))

                  if (!search) return options

                  return options.filter((opt) =>
                    opt.label.toLowerCase().includes(search.toLowerCase())
                  )
                }}
                label="Estado"
                name="status"
                placeholder="Seleccionar"
                searchPlaceholder="Buscar estado..."
                showLabel
              />
            </div>

            {/* Nombre del contenedor - Número de operación */}
            <div className="flex flex-col gap-3 md:flex-row md:gap-4">
              {/* Nombre del contenedor */}
              <div className="flex-1">
                <FormInput<EditDetailsFormData>
                  control={control}
                  label="Nombre del contenedor"
                  name="name"
                  placeholder="Ingresar nombre..."
                  rules={{
                    required: 'El nombre del contenedor es obligatorio',
                  }}
                  showLabel
                />
              </div>

              {/* Número de operación */}
              <div className="flex-1">
                <FormInput<EditDetailsFormData>
                  control={control}
                  label="Número de operación"
                  name="operationNumber"
                  placeholder="Ingresar número..."
                  rules={{
                    required: 'El número de operación es obligatorio',
                  }}
                  showLabel
                />
              </div>
            </div>

            {/* País de origen - Categorías */}
            <div className="flex flex-col gap-3 md:flex-row md:gap-4">
              {/* País de origen */}
              <div className="flex flex-1 items-end gap-2">
                <div className="flex-1">
                  <FormSearchableSelect<EditDetailsFormData>
                    control={control}
                    defaultValue={defaultCountry}
                    fetcher={fetchCountries}
                    label="País de origen"
                    name="countryFromId"
                    placeholder="Seleccionar país"
                    searchPlaceholder="Buscar país..."
                    showLabel
                  />
                </div>
                {onAddCountry && (
                  <button
                    className={
                      'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md ' +
                      'border border-neutral-300 bg-neutral-50 shadow-sm' +
                      'transition-colors hover:bg-neutral-100'
                    }
                    onClick={onAddCountry}
                    type="button"
                  >
                    <PlusIcon className="h-4 w-4 text-neutral-800" />
                  </button>
                )}
              </div>

              {/* Categorías */}
              <div className="flex flex-1 items-end gap-2">
                <div className="flex-1">
                  <FormSearchableSelect<EditDetailsFormData>
                    control={control}
                    defaultValue={defaultCategories}
                    fetcher={fetchCategories}
                    label="Categorías"
                    multiple
                    name="categories"
                    placeholder="Seleccionar categorías"
                    searchPlaceholder="Buscar categorías..."
                    showLabel
                  />
                </div>
                {onAddCategory && (
                  <button
                    className={
                      'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md ' +
                      'border border-neutral-300 bg-neutral-50 shadow-sm' +
                      'transition-colors hover:bg-neutral-100'
                    }
                    onClick={onAddCategory}
                    type="button"
                  >
                    <PlusIcon className="h-4 w-4 text-neutral-800" />
                  </button>
                )}
              </div>
            </div>

            {/* Retornado */}
            <div className="flex items-center gap-2">
              <FormSwitch
                control={control}
                label="Retornado"
                name="isReturned"
              />
            </div>

            {/* Compañía proveedora - Operador logístico */}
            <div className="flex flex-col gap-3 md:flex-row md:gap-4">
              {/* Compañía proveedora */}
              <div className="flex-1">
                <FormSearchableSelect<EditDetailsFormData>
                  control={control}
                  defaultValue={defaultOwnerCompany}
                  fetcher={fetchCompanies}
                  label="Compañía proveedora"
                  name="ownerCompanyId"
                  placeholder="Seleccionar compañía"
                  searchPlaceholder="Buscar compañía..."
                  showLabel
                />
              </div>

              {/* Operador logístico */}
              <div className="flex-1">
                <FormSearchableSelect<EditDetailsFormData>
                  control={control}
                  defaultValue={defaultManagerCompany}
                  fetcher={fetchManagerCompanies}
                  label="Operador logístico"
                  name="managerCompanyId"
                  placeholder="Seleccionar operador"
                  searchPlaceholder="Buscar operador..."
                  showLabel
                />
              </div>
            </div>

            {/* Observaciones */}
            <div className="flex flex-col gap-2">
              <FormTextarea
                control={control}
                label="Observaciones"
                name="observations"
                placeholder="Ingresar observaciones..."
                rows={4}
                showLabel
              />
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="flex-shrink-0 border-t border-neutral-300 p-3 md:p-4">
            <Button
              className="w-full md:w-auto"
              disabled={isPending}
              form="edit-details-form"
              type="submit"
              variant="default"
            >
              {isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
